// =============================================================================
// VandieZ admin-web -- Promotion detail (/promotions/:code)
// =============================================================================
// Single-program drill-down for the ops team. Three tabs:
//
//   Config       — kind-specific form (prize_amount, is_active,
//                  effective_to, and per-kind config fields).
//                  Saves via PATCH /admin/promotions/:code.
//   Awards       — paginated list of awards for the program with
//                  status pill + per-row metadata.
//   Manual rerun — period + dry-run preview + commit. ONLY shown
//                  for monthly programs (driver_of_month +
//                  esteemer_of_month). The settle endpoint accepts
//                  any code, but the human workflow only needs a
//                  manual re-trigger for the monthly winners.
//
// Mirrors PromotionsIndexPage's data-fetch + ApiError → toast pattern.
// (Spec 7, Task 31.)
// =============================================================================

import { AlertCircle, ArrowLeft, Gift } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { promotionsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { ApiError } from "@/types/auth";
import type {
  PromotionAwardRead,
  PromotionProgramRead,
  SettleResponse,
} from "@/types/promotion";

type TabKey = "config" | "awards" | "rerun";

const MONTHLY_KINDS = new Set(["driver_of_month", "esteemer_of_month"]);

function isMonthlyKind(kind: string): boolean {
  return MONTHLY_KINDS.has(kind);
}

function statusPillClass(status: PromotionAwardRead["status"]): string {
  switch (status) {
    case "settled":
      return "bg-success-subtle text-success-subtle-foreground";
    case "pending":
      return "bg-warning-subtle text-warning-subtle-foreground";
    case "clawed_back":
      return "bg-danger-subtle text-danger-subtle-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

interface ConfigFormProps {
  program: PromotionProgramRead;
  saving: boolean;
  onSave: (payload: {
    prize_amount?: string;
    config?: Record<string, unknown>;
    is_active?: boolean;
    effective_to?: string | null;
  }) => void;
}

function ConfigForm({
  program,
  saving,
  onSave,
}: ConfigFormProps): React.JSX.Element {
  const [prizeAmount, setPrizeAmount] = useState<string>(program.prize_amount);
  const [isActive, setIsActive] = useState<boolean>(program.is_active);
  const [effectiveTo, setEffectiveTo] = useState<string>(
    program.effective_to ?? "",
  );

  // Kind-specific config knobs. We keep the values as strings to bind
  // straight into <Input/>, then parse on submit. Index-signature access
  // uses bracket notation to satisfy TS noUncheckedIndexedAccess.
  const [maxFreeTrips, setMaxFreeTrips] = useState<string>(
    typeof program.config["max_free_trips"] === "number"
      ? String(program.config["max_free_trips"])
      : "",
  );
  const [minTrips, setMinTrips] = useState<string>(
    typeof program.config["min_trips"] === "number"
      ? String(program.config["min_trips"])
      : "",
  );
  const [minPostings, setMinPostings] = useState<string>(
    typeof program.config["min_postings"] === "number"
      ? String(program.config["min_postings"])
      : "",
  );
  const [referralBonus, setReferralBonus] = useState<string>(
    typeof program.config["referral_bonus"] === "string" ||
      typeof program.config["referral_bonus"] === "number"
      ? String(program.config["referral_bonus"])
      : "",
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const config: Record<string, unknown> = { ...program.config };
    switch (program.kind) {
      case "launch_promo":
        if (maxFreeTrips.trim().length > 0) {
          const parsed = Number.parseInt(maxFreeTrips, 10);
          if (Number.isFinite(parsed)) {
            config["max_free_trips"] = parsed;
          }
        }
        break;
      case "driver_of_month":
        if (minTrips.trim().length > 0) {
          const parsed = Number.parseInt(minTrips, 10);
          if (Number.isFinite(parsed)) {
            config["min_trips"] = parsed;
          }
        }
        break;
      case "esteemer_of_month":
        if (minPostings.trim().length > 0) {
          const parsed = Number.parseInt(minPostings, 10);
          if (Number.isFinite(parsed)) {
            config["min_postings"] = parsed;
          }
        }
        break;
      case "customer_referral":
      case "driver_referral":
        if (referralBonus.trim().length > 0) {
          config["referral_bonus"] = referralBonus;
        }
        break;
      default:
        break;
    }
    onSave({
      prize_amount: prizeAmount,
      is_active: isActive,
      effective_to: effectiveTo.trim().length === 0 ? null : effectiveTo,
      config,
    });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="prize_amount">Prize amount (INR)</Label>
          <Input
            id="prize_amount"
            type="text"
            value={prizeAmount}
            onChange={(e) => { setPrizeAmount(e.target.value); }}
            placeholder="0"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="effective_to">Effective to (UTC, optional)</Label>
          <Input
            id="effective_to"
            type="text"
            value={effectiveTo}
            onChange={(e) => { setEffectiveTo(e.target.value); }}
            placeholder="2026-12-31T23:59:59Z"
          />
        </div>

        {program.kind === "launch_promo" && (
          <div className="space-y-1">
            <Label htmlFor="max_free_trips">Max free trips</Label>
            <Input
              id="max_free_trips"
              type="number"
              value={maxFreeTrips}
              onChange={(e) => { setMaxFreeTrips(e.target.value); }}
              placeholder="5"
            />
          </div>
        )}

        {program.kind === "driver_of_month" && (
          <div className="space-y-1">
            <Label htmlFor="min_trips">Min trips to qualify</Label>
            <Input
              id="min_trips"
              type="number"
              value={minTrips}
              onChange={(e) => { setMinTrips(e.target.value); }}
              placeholder="20"
            />
          </div>
        )}

        {program.kind === "esteemer_of_month" && (
          <div className="space-y-1">
            <Label htmlFor="min_postings">Min postings to qualify</Label>
            <Input
              id="min_postings"
              type="number"
              value={minPostings}
              onChange={(e) => { setMinPostings(e.target.value); }}
              placeholder="10"
            />
          </div>
        )}

        {(program.kind === "customer_referral" ||
          program.kind === "driver_referral") && (
          <div className="space-y-1">
            <Label htmlFor="referral_bonus">Referral bonus (INR)</Label>
            <Input
              id="referral_bonus"
              type="text"
              value={referralBonus}
              onChange={(e) => { setReferralBonus(e.target.value); }}
              placeholder="200"
            />
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => { setIsActive(e.target.checked); }}
          className="h-4 w-4 rounded border-input"
        />
        Program is active
      </label>

      <details className="rounded border bg-muted/30 p-3 text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          Raw config JSON (read-only)
        </summary>
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all">
          {JSON.stringify(program.config, null, 2)}
        </pre>
      </details>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}

function AwardsTab({
  code,
  accessToken,
}: {
  code: string;
  accessToken: string;
}): React.JSX.Element {
  const toast = useToast();
  const [awards, setAwards] = useState<PromotionAwardRead[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 50;

  const fetchAwards = useCallback(
    async (currOffset: number, signal?: AbortSignal): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const res = await promotionsAdminApi.awards(
          accessToken,
          code,
          { limit, offset: currOffset },
          signal,
        );
        setAwards(res.awards);
        setTotal(res.total);
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
          toast.error(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // silent unmount
        } else {
          const msg = "Unexpected error loading awards.";
          setError(msg);
          toast.error(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, code, toast],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchAwards(offset, controller.signal);
    return () => { controller.abort(); };
  }, [fetchAwards, offset]);

  const pageInfo = useMemo(() => {
    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + awards.length, total);
    return `${start}–${end} of ${total}`;
  }, [offset, awards.length, total]);

  return (
    <div className="space-y-3">
      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && awards.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground">Loading…</div>
      ) : awards.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground">
          No awards have been issued yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2 pr-4">User</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Period</th>
                <th className="py-2 pr-4 text-right">Amount</th>
                <th className="py-2 pr-4">Awarded</th>
              </tr>
            </thead>
            <tbody>
              {awards.map((a) => (
                <tr
                  key={a.id}
                  className="border-b last:border-b-0 hover:bg-muted/40"
                >
                  <td className="py-2 pr-4">
                    <div className="font-medium">
                      {a.user_full_name ?? a.user_id}
                    </div>
                    {a.rank !== null && (
                      <div className="text-xs text-muted-foreground">
                        Rank #{a.rank}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(a.status)}`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-xs">
                    {a.period_start !== null && a.period_end !== null
                      ? `${a.period_start.slice(0, 10)} → ${a.period_end.slice(0, 10)}`
                      : "—"}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {a.amount}
                  </td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">
                    {a.awarded_at.slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{pageInfo}</span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || loading}
            onClick={() => { setOffset(Math.max(0, offset - limit)); }}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + awards.length >= total || loading}
            onClick={() => { setOffset(offset + limit); }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function RerunTab({
  code,
  accessToken,
}: {
  code: string;
  accessToken: string;
}): React.JSX.Element {
  const toast = useToast();
  const [period, setPeriod] = useState<string>(() => {
    // Default to "previous month" in YYYY-MM form (UTC).
    const now = new Date();
    const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const y = prev.getUTCFullYear();
    const m = String(prev.getUTCMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  });
  const [preview, setPreview] = useState<SettleResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [committing, setCommitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const runSettle = async (dryRun: boolean): Promise<void> => {
    setError(null);
    if (dryRun) {
      setLoading(true);
    } else {
      setCommitting(true);
    }
    try {
      const res = await promotionsAdminApi.settle(accessToken, code, {
        period,
        dry_run: dryRun,
      });
      setPreview(res);
      if (!dryRun) {
        toast.success(
          `Awarded ${res.awarded_count} winner${res.awarded_count === 1 ? "" : "s"} for ${res.period_start.slice(0, 7)}.`,
        );
      }
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        setError(e.problem.detail);
        toast.error(e.problem.detail);
      } else {
        const msg = "Unexpected error running settlement.";
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
      setCommitting(false);
    }
  };

  const handleConfirm = (): void => {
    const ok = window.confirm(
      `Commit awards for ${period}? This writes ledger entries and cannot be reversed without a clawback.`,
    );
    if (!ok) {return;}
    void runSettle(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="period">Period (YYYY-MM)</Label>
          <Input
            id="period"
            type="text"
            value={period}
            onChange={(e) => { setPeriod(e.target.value); }}
            placeholder="2026-05"
          />
        </div>
        <Button
          variant="outline"
          disabled={loading}
          onClick={() => { void runSettle(true); }}
        >
          {loading ? "Loading…" : "Dry-run preview"}
        </Button>
      </div>

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {preview !== null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {preview.awarded ? "Awards committed" : "Dry-run candidates"}
            </CardTitle>
            <CardDescription>
              {preview.period_start.slice(0, 10)} →{" "}
              {preview.period_end.slice(0, 10)} ·{" "}
              {preview.candidates.length} candidate
              {preview.candidates.length === 1 ? "" : "s"}
              {preview.awarded
                ? ` · ${preview.awarded_count} awarded`
                : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {preview.candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No candidates met the qualifying criteria for this period.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Rank</th>
                      <th className="py-2 pr-4">User</th>
                      <th className="py-2 pr-4 text-right">Trips</th>
                      <th className="py-2 pr-4 text-right">Postings</th>
                      <th className="py-2 pr-4 text-right">Rating</th>
                      <th className="py-2 pr-4 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.candidates.map((c, idx) => (
                      <tr
                        key={`${c.user_id}-${idx}`}
                        className="border-b last:border-b-0"
                      >
                        <td className="py-2 pr-4">{c.rank ?? idx + 1}</td>
                        <td className="py-2 pr-4 font-medium">{c.full_name}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          {c.trip_count ?? "—"}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          {c.posting_count ?? "—"}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          {c.avg_rating ?? "—"}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          {c.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!preview.awarded && preview.candidates.length > 0 && (
              <div className="mt-4 flex justify-end">
                <Button
                  variant="default"
                  disabled={committing}
                  onClick={handleConfirm}
                >
                  {committing ? "Committing…" : "Commit awards"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function PromotionDetailPage(): React.JSX.Element {
  const { code = "" } = useParams<{ code: string }>();
  const { accessToken } = useAuth();
  const toast = useToast();

  const [program, setProgram] = useState<PromotionProgramRead | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("config");

  const fetchProgram = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null || code.length === 0) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await promotionsAdminApi.get(accessToken, code, signal);
        setProgram(res);
      } catch (e: unknown) {
        if (e instanceof ApiError) {
          setError(e.problem.detail);
          toast.error(e.problem.detail);
        } else if (e instanceof Error && e.name === "AbortError") {
          // silent unmount
        } else {
          const msg = "Unexpected error loading promotion program.";
          setError(msg);
          toast.error(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, code, toast],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchProgram(controller.signal);
    return () => { controller.abort(); };
  }, [fetchProgram]);

  const handleSave = async (payload: {
    prize_amount?: string;
    config?: Record<string, unknown>;
    is_active?: boolean;
    effective_to?: string | null;
  }): Promise<void> => {
    if (accessToken === null) {return;}
    setSaving(true);
    try {
      const updated = await promotionsAdminApi.patch(
        accessToken,
        code,
        payload,
      );
      setProgram(updated);
      toast.success("Saved promotion configuration.");
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        toast.error(e.problem.detail);
      } else {
        toast.error("Unexpected error saving promotion.");
      }
    } finally {
      setSaving(false);
    }
  };

  const showRerun =
    program !== null && isMonthlyKind(program.kind);

  return (
    <div className="space-y-4">
      <PageHeader
        title={program?.code ?? code}
        description={
          program !== null
            ? `${program.kind} · prize ${program.prize_amount}`
            : "Loading…"
        }
        actions={
          <Link to="/promotions">
            <Button variant="outline">
              <ArrowLeft className="mr-1 h-4 w-4" />
              All programs
            </Button>
          </Link>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {program === null ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {loading ? "Loading…" : "Program not found."}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Gift className="h-4 w-4 text-success" />
              {program.code}
            </CardTitle>
            <CardDescription>
              {program.is_active ? "Active" : "Paused"} ·{" "}
              {program.effective_to === null
                ? "no expiry"
                : `expires ${program.effective_to.slice(0, 10)}`}
            </CardDescription>
            <div className="mt-3 flex gap-2">
              <Button
                variant={tab === "config" ? "default" : "outline"}
                size="sm"
                onClick={() => { setTab("config"); }}
              >
                Config
              </Button>
              <Button
                variant={tab === "awards" ? "default" : "outline"}
                size="sm"
                onClick={() => { setTab("awards"); }}
              >
                Awards
              </Button>
              {showRerun && (
                <Button
                  variant={tab === "rerun" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setTab("rerun"); }}
                >
                  Manual rerun
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {tab === "config" && (
              <ConfigForm
                program={program}
                saving={saving}
                onSave={(payload) => { void handleSave(payload); }}
              />
            )}
            {tab === "awards" && accessToken !== null && (
              <AwardsTab code={code} accessToken={accessToken} />
            )}
            {tab === "rerun" && showRerun && accessToken !== null && (
              <RerunTab code={code} accessToken={accessToken} />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default PromotionDetailPage;
